import { lazy, Suspense } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import FormationsPage from './pages/FormationsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import DemandeInfo from './pages/DemandeInfo';
import Certification from './pages/Certification';
import FormationIAParisPage from './pages/FormationIAParisPage';
import DiagnosticPage from './pages/DiagnosticPage';
import AccompagnementPage from './pages/AccompagnementPage';
import QuestionnairePage from './pages/QuestionnairePage';
import LandingPage from './pages/LandingPage';
import PolitiqueConfidentialitePage from './pages/PolitiqueConfidentialitePage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import BlogArticle from './pages/BlogArticle';
import { LEGACY_SLUG_TO_ID, DEFAULT_VARIANT_ID } from './data/landingPages';

const BlogPage = lazy(() => import('./pages/BlogPage'));
const LazyBlogPage = () => <Suspense fallback={null}><BlogPage /></Suspense>;

const FormationDetailPage = lazy(() => import('./pages/FormationDetailPage'));
const LazyFormationDetailPage = () => <Suspense fallback={null}><FormationDetailPage /></Suspense>;

// Redirect wrappers for dynamic routes
const BlogSlugRedirect = () => {
    const { slug } = useParams<{ slug: string }>();
    return <Navigate to={`/blog/${slug}/`} replace />;
};

const FormationIASlugRedirect = () => {
    const { slug } = useParams<{ slug: string }>();
    return <Navigate to={`/formationIA/${slug}/`} replace />;
};

const CertificationIdRedirect = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/certification/${id}/`} replace />;
};

const LPSlugRedirect = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const id = (slug && LEGACY_SLUG_TO_ID[slug]) ?? DEFAULT_VARIANT_ID;

    const params = new URLSearchParams(searchParams);
    params.set('id', id);

    return <Navigate to={`/lp/?${params.toString()}`} replace />;
};

export const routes: RouteRecord[] = [
    {
        path: '/',
        element: <Layout />,
        children: [
            {
                index: true,
                element: <HomePage />,
            },
            {
                path: 'formation-ia-entreprise/',
                element: <FormationsPage />,
            },
            {
                path: 'formation-ia-paris/',
                element: <FormationIAParisPage />,
            },
            {
                path: 'accompagnement-ia-entreprise/',
                element: <AccompagnementPage />,
            },
            {
                path: 'about/',
                element: <AboutPage />,
            },
            {
                path: 'blog/',
                element: <LazyBlogPage />,
            },
            {
                path: 'blog/:slug/',
                element: <BlogArticle />,
            },
            {
                path: 'formationIA/:slug/',
                element: <LazyFormationDetailPage />,
            },
            {
                path: 'contact/',
                element: <ContactPage />,
            },
            {
                path: 'DemandeInfo/',
                element: <DemandeInfo />,
            },
            {
                path: 'demarrer-votre-diagnostic-ia-entreprise/',
                element: <DiagnosticPage />,
            },
            {
                path: 'formation-ia-entreprise/questionnaire/',
                element: <QuestionnairePage />,
            },
            {
                path: 'certification/',
                element: <Certification />,
            },
            {
                path: 'certification/:id/',
                element: <Certification />,
            },
            {
                path: 'politique-de-confidentialite/',
                element: <PolitiqueConfidentialitePage />,
            },
            {
                path: 'mentions-legales/',
                element: <MentionsLegalesPage />,
            },
            {
                path: '404',
                element: <NotFoundPage />,
            },
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
    {
        path: 'lp/:slug/',
        element: <LPSlugRedirect />,
    },
    {
        path: 'lp/',
        element: <LandingPage />,
    },
    // Redirects from URLs without trailing slashes to URLs with trailing slashes
    {
        path: 'formation-ia-entreprise',
        element: <Navigate to="/formation-ia-entreprise/" replace />,
    },
    {
        path: 'formation-ia-paris',
        element: <Navigate to="/formation-ia-paris/" replace />,
    },
    {
        path: 'accompagnement-ia-entreprise',
        element: <Navigate to="/accompagnement-ia-entreprise/" replace />,
    },
    {
        path: 'about',
        element: <Navigate to="/about/" replace />,
    },
    {
        path: 'blog',
        element: <Navigate to="/blog/" replace />,
    },
    {
        path: 'blog/:slug',
        element: <BlogSlugRedirect />,
    },
    {
        path: 'formationIA/:slug',
        element: <FormationIASlugRedirect />,
    },
    {
        path: 'contact',
        element: <Navigate to="/contact/" replace />,
    },
    {
        path: 'DemandeInfo',
        element: <Navigate to="/DemandeInfo/" replace />,
    },
    {
        path: 'demarrer-votre-diagnostic-ia-entreprise',
        element: <Navigate to="/demarrer-votre-diagnostic-ia-entreprise/" replace />,
    },
    {
        path: 'formation-ia-entreprise/questionnaire',
        element: <Navigate to="/formation-ia-entreprise/questionnaire/" replace />,
    },
    {
        path: 'certification',
        element: <Navigate to="/certification/" replace />,
    },
    {
        path: 'certification/:id',
        element: <CertificationIdRedirect />,
    },
    {
        path: 'politique-de-confidentialite',
        element: <Navigate to="/politique-de-confidentialite/" replace />,
    },
    {
        path: 'mentions-legales',
        element: <Navigate to="/mentions-legales/" replace />,
    },
    {
        path: 'lp/:slug',
        element: <LPSlugRedirect />,
    },
    {
        path: 'lp',
        element: <Navigate to="/lp/" replace />,
    },
];
